//
// Copyright (c) 2011-2025 by Curalate, Inc.
//

import React, { useEffect, useState } from 'react';
import { useFormContext } from 'react-hook-form';
import styled from 'styled-components';

import { CampaignRequest } from '@apis/campaign-requests/shared/CampaignRequests';
import { CAMPAIGN_REQUEST_FORM_VALIDATION } from '@campaigns/constants/CampaignRequestFormValidationConstants';
import { CAMPAIGNS_TRACKING_ID } from '@campaigns/constants/CampaignsTrackingId';
import Button from '@components/atoms/button/Button';
import { DefaultButtonStyles } from '@components/atoms/button/buttonStyles';
import Icon from '@components/atoms/icon/Icon';
import LabeledCheckbox from '@components/atoms/labeled-checkbox/LabeledCheckbox';
import { CaptionErrorText } from '@components/atoms/text/Text';
import { FormFieldController } from '@components/form/field-controller/FormFieldController';
import { IDropdownSelectItem } from '@components/molecules/dropdown-select/DropdownSelect';
import SearchBar from '@components/molecules/search-bar/SearchBar';
import { DropdownMenu } from '@components/primitives/dropdown-menu/DropdownMenu';
import { BazaarvoiceClientId } from '@models/BazaarvoiceClientId';
import { isDefined, isTruthy, isUndefined } from '@utils/filterPredicates';

interface Props {
  retailerOptions: IDropdownSelectItem<BazaarvoiceClientId>[];
  shouldShowCheckbox?: boolean;
}

// Caps the dropdown at 40vh or 320px — matches the typeahead dropdowns.
// The previous mediumMeasure (440px) was too tall at short viewports / high browser zoom.
const DROPDOWN_MAX_HEIGHT = 'min(320px, 40vh)';

export const CampaignRequestRetailerField = ({
  retailerOptions,
  // TODO - PD-312744 Remove shouldShowCheckbox and all related logic once prepaid feature is fully launched to call clients
  shouldShowCheckbox = true,
}: Props) => {
  const { control, setValue, watch } = useFormContext<CampaignRequest>();
  const formValueRetailer = watch('targetClientId');
  const [isRetailerCheckboxChecked, setIsRetailerCheckboxChecked] = useState(
    isDefined(formValueRetailer) && formValueRetailer !== '',
  );
  const [isOpen, setIsOpen] = useState(false);
  const [searchTerm, setSearchTerm] = useState('');

  useEffect(() => {
    setIsRetailerCheckboxChecked(
      isDefined(formValueRetailer) && formValueRetailer !== '',
    );
  }, [formValueRetailer]);

  const isSubstring = (term: string, label: string) =>
    label.toLocaleLowerCase().includes(term.toLocaleLowerCase());

  return (
    <Container $hasCheckbox={shouldShowCheckbox}>
      {shouldShowCheckbox && (
        <LabeledCheckbox
          handleClick={(checked) => {
            setIsRetailerCheckboxChecked(checked);
            if (!checked) {
              setValue('targetClientId', '');
            } else {
              if (isUndefined(formValueRetailer) || formValueRetailer === '') {
                setValue('targetClientId', undefined);
              }
            }
          }}
          helpText='You may select a single retailer, who will receive all content generated as part of this campaign.'
          isChecked={isRetailerCheckboxChecked}
          label='Send content to a single retailer'
          trackingId={
            CAMPAIGNS_TRACKING_ID.CREATE_SAMPLING_CAMPAIGN_RETAILERS_CHECKBOX
          }
        />
      )}
      {(!shouldShowCheckbox || isRetailerCheckboxChecked) && (
        <FormFieldController
          control={control}
          name='targetClientId'
          rules={CAMPAIGN_REQUEST_FORM_VALIDATION.targetClientId}
        >
          {({ error, ...props }) => {
            const selectedValue =
              isTruthy(props.value) && props.value !== ''
                ? props.value.toString()
                : undefined;

            const selectedLabel = retailerOptions.find(
              (o) => o.value?.toString() === selectedValue,
            )?.label;

            const selectContent = (
              <>
                <DropdownMenu
                  onOpenChange={(open) => {
                    setIsOpen(open);
                    if (!open) { setSearchTerm(''); }
                  }}
                  open={isOpen}
                >
                  <DropdownMenu.Trigger asChild>
                    <Button
                      buttonStyles={DefaultButtonStyles}
                      isDropdown={true}
                    >
                      {selectedLabel ?? 'Select a retailer'}
                    </Button>
                  </DropdownMenu.Trigger>
                  <StyledDropdownContent align='start'>
                    <StyledSearchBar
                      autoFocus
                      onChange={setSearchTerm}
                      placeholder='Search'
                      trackingIdPrefix={
                        CAMPAIGNS_TRACKING_ID.CREATE_SAMPLING_CAMPAIGN_RETAILERS_SEARCH_BAR
                      }
                      value={searchTerm}
                      visuallyHiddenLabel='Search retailers'
                    />
                    <ScrollableList>
                      <DropdownMenu.RadioGroup
                        onValueChange={(value) => {
                          props.onChange(BazaarvoiceClientId(value));
                          setSearchTerm('');
                          setIsOpen(false);
                        }}
                        value={selectedValue}
                      >
                        {retailerOptions
                          .filter(
                            (option) =>
                              isDefined(option.value) &&
                              isSubstring(searchTerm, option.label),
                          )
                          .map((option) => {
                            const value = option.value;
                            if (!isDefined(value)) { return null; }
                            return (
                              <DropdownMenu.RadioItem
                                key={option.label}
                                trackingId={
                                  CAMPAIGNS_TRACKING_ID.CREATE_SAMPLING_CAMPAIGN_RETAILERS_SEARCH_BAR
                                }
                                value={value.toString()}
                              >
                                <DropdownMenu.ItemIndicator>
                                  <DropdownMenu.IndicatorIcon />
                                </DropdownMenu.ItemIndicator>
                                {option.label}
                              </DropdownMenu.RadioItem>
                            );
                          })}

                      </DropdownMenu.RadioGroup>
                    </ScrollableList>
                  </StyledDropdownContent>
                </DropdownMenu>
                {error && (
                  <ErrorText>
                    <StyledIcon name='error' />
                    <CaptionErrorText>{error.message}</CaptionErrorText>
                  </ErrorText>
                )}
              </>
            );

            return shouldShowCheckbox ? (
              <DropdownMenuContainer>{selectContent}</DropdownMenuContainer>
            ) : (
              selectContent
            );
          }}
        </FormFieldController>
      )}
    </Container>
  );
};

const Container = styled.div<{ $hasCheckbox: boolean }>`
  ${({ $hasCheckbox, theme }) => `
    margin-top: ${$hasCheckbox ? theme.spacings[3] : 0};
    gap: ${theme.spacings[2]};
  `}
  display: flex;
  flex-direction: column;
`;

const DropdownMenuContainer = styled.div`
  margin-left: 24px;
  max-width: 330px;
`;

const StyledSearchBar = styled(SearchBar)`
  padding: 0 ${({ theme }) => theme.spacings[2]};
  flex-shrink: 0;
`;

// Flex-column layout pins the search bar above the scrollable list.
const StyledDropdownContent = styled(DropdownMenu.Content)`
  display: flex;
  flex-direction: column;
  max-height: ${DROPDOWN_MAX_HEIGHT};
  overflow: hidden;
`;

// flex: 1 and min-height: 0 mirror the ScrollableList pattern in TypeaheadDropdownContent.
const ScrollableList = styled.div`
  flex: 1;
  min-height: 0;
  overflow-y: auto;
`;

const ErrorText = styled.div`
  display: flex;
  align-items: center;
`;

const StyledIcon = styled(Icon)(
  ({ theme }) => `
    margin-right: ${theme.spacings[1]};
    color: ${theme.colors.aliases.error};
    height: calc(${theme.spacings[1]} * 3);
    width: calc(${theme.spacings[1]} * 3);
`,
);
